import { getUnixTime, subDays } from 'date-fns';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  NavigateOptions,
  URLSearchParamsInit,
  useSearchParams,
} from 'react-router-dom';

import { isEmpty } from './typeCheck';
function isObject(value: any) {
  return Object(value) === value;
}

const queryParamMapping = {
  lifecycleIds: 'lifecycleStage',
  clientStatus: 'status',
  subTrainerId: 'trainer',
  customGroupId: 'group',
  planId: 'workoutPlan',
};

const filterPrefix = 'f_';

export interface ListParams {
  page: number;
  perPage: number;
  search: string;
  sortBy: string;
  sortDir: 'asc' | 'desc';
}

interface useListSearchParamsOptions<F extends boolean | undefined = true> {
  paramPrefix?: string;
  defaultPerPage?: number;
  /**
   * Whether to sync with URL query parameters (default: true):
   * - true: Parameters are read from the URL and synced to the URL when updated (the address bar will change)
   * - false: Parameters are stored in an in-memory Map and do not affect the URL
   */
  syncWithUrl?: F;
}

type SearchParamsMap = {
  true: {
    get: URLSearchParams;
    set: (
      nextInit?:
        | URLSearchParamsInit
        | ((prev: URLSearchParams) => URLSearchParamsInit),
      navigateOpts?: NavigateOptions
    ) => void;
  };
  false: {
    get: Map<string, any>;
    set: Dispatch<SetStateAction<Map<string, any>>>;
  };
};

type SearchParamsType<T extends boolean | undefined = true> =
  SearchParamsMap[T extends false ? 'false' : 'true']['get'];

type SetSearchParamsType<T extends boolean | undefined = true> =
  SearchParamsMap[T extends false ? 'false' : 'true']['set'];

export function useListSearchParams<T extends boolean | undefined = true>({
  paramPrefix = '',
  defaultPerPage = 20,
  syncWithUrl = true,
}: useListSearchParamsOptions<T> = {}) {
  const [searchParamsMap, setSearchParamsMap] = useState(
    new Map<string, any>()
  );
  const [searchParamsUrl, setSearchParamsUrl] = useSearchParams();

  const searchParams = (
    syncWithUrl ? searchParamsUrl : searchParamsMap
  ) as SearchParamsType<T>;
  const setSearchParams = (
    syncWithUrl ? setSearchParamsUrl : setSearchParamsMap
  ) as SetSearchParamsType<T>;

  const params: ListParams = {
    page: Number(searchParams.get(paramPrefix + 'page')) || 1,
    perPage:
      Number(searchParams.get(paramPrefix + 'perPage')) || defaultPerPage,
    search: searchParams.get(paramPrefix + 'search') || '',
    sortBy: searchParams.get(paramPrefix + 'sortBy') || '',
    sortDir: (searchParams.get(paramPrefix + 'sortDir') || 'desc') as
      | 'asc'
      | 'desc',
  };

  const getAllFilterParams = useCallback(() => {
    const filterParams = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith(filterPrefix) || key.startsWith(paramPrefix)) {
        const keyWithNoPrefixes = key
          .replace(filterPrefix, '')
          .replace(paramPrefix, '');
        try {
          const parsedObject = JSON.parse(decodeURI(value || ''));
          filterParams[keyWithNoPrefixes] = parsedObject;
          return;
        } catch (e) {
          filterParams[keyWithNoPrefixes] = value;
          return;
        }
      }
    });
    return filterParams as any;
  }, [paramPrefix, searchParams]);

  const filterParams = useMemo(() => {
    return getAllFilterParams();
  }, [getAllFilterParams]);

  const removeParam = useCallback(
    (key: string) => {
      if (syncWithUrl) {
        searchParams.delete(paramPrefix + key);
        setSearchParams(searchParams as any, { replace: true });
        return;
      }
      (setSearchParams as SearchParamsMap['false']['set'])((pre) => {
        const newParams = new Map(pre);
        newParams.delete(paramPrefix + key);
        return newParams;
      });
    },
    [syncWithUrl, paramPrefix, searchParams, setSearchParams]
  );

  const updateParam = useCallback(
    (key: string, value?: any) => {
      if (isEmpty(value)) {
        removeParam(key);
        return;
      }
      if (syncWithUrl) {
        setSearchParams(
          (params) => {
            if (key !== 'page') {
              params.set(paramPrefix + 'page', '1');
            }
            params.set(paramPrefix + key, value);
            return params;
          },
          { replace: true }
        );
        return;
      }
      (setSearchParams as SearchParamsMap['false']['set'])((p) => {
        const params = new Map(p);
        if (key !== 'page') {
          params.set(paramPrefix + 'page', '1');
        }
        params.set(paramPrefix + key, value);
        return params;
      });
    },
    [syncWithUrl, paramPrefix, removeParam, setSearchParams]
  );

  const updateFilterParam = useCallback(
    (filterField, newValue) => {
      if (Array.isArray(newValue) && newValue.length === 0) {
        removeParam(filterPrefix + filterField);
        return;
      }
      if (isObject(newValue)) {
        const encoded = encodeURI(JSON.stringify(newValue));
        updateParam(filterPrefix + filterField, encoded);
        return;
      }
      updateParam(filterPrefix + filterField, newValue);
    },
    [updateParam, removeParam]
  );

  const getFilterParamValue = useCallback(
    (filterField) => {
      const baseValue = searchParams.get(
        paramPrefix + filterPrefix + filterField
      );
      try {
        const parsedObject = JSON.parse(decodeURI(baseValue || ''));
        return parsedObject;
      } catch (e) {
        return baseValue;
      }
    },
    [paramPrefix, searchParams]
  );

  const resetFilterParams = useCallback(() => {
    Object.keys(filterParams).forEach((key) => {
      removeParam(filterPrefix + key);
    });
  }, [filterParams, removeParam]);

  const getQuickLinkString = useCallback(() => {
    const filters = {
      search: params.search,
      isV2: 'true',
    };
    Object.keys(filterParams).forEach((key) => {
      const value = filterParams[key];
      if (isObject(value)) {
        const encoded = encodeURI(JSON.stringify(value));
        filters[key] = encoded;
        return;
      }
      filters[key] = value;
    });
    const filtersAsString = new URLSearchParams(filters).toString();
    return filtersAsString;
  }, [filterParams, params.search]);

  const updateFromQuickLink = useCallback(
    (object) => {
      resetFilterParams();
      const customProfileFieldField = object.profileField;
      const customProfileFieldValue = object.profileFieldSearch;
      const filterNumber = object.filterNumber;

      if (customProfileFieldField && customProfileFieldValue) {
        updateFilterParam('customProfileField', {
          field: customProfileFieldField,
          value: customProfileFieldValue,
        });
      }

      Object.keys(object).forEach((key) => {
        if (['profileField', 'profileFieldSearch'].includes(key)) {
          return;
        }

        let newKey = key;
        let newValue = object[key];

        if (key in queryParamMapping) {
          newKey = queryParamMapping[key];
        }

        if (key === 'search') {
          updateParam(key, object[key]);
          return;
        }

        if (
          !object.isV2 &&
          newKey === 'lifecycleStage' &&
          newValue &&
          typeof newValue === 'string'
        ) {
          newValue = newValue.split(',').map(Number);
        }

        if (
          !object.isV2 &&
          newKey === 'customStatus' &&
          newValue &&
          typeof newValue === 'string'
        ) {
          newValue = {
            value: newValue,
            number: filterNumber,
            start: getUnixTime(subDays(new Date(), new Date().getDay() + 7)),
            end: getUnixTime(new Date()),
          };
        }

        const tags = getFilterParamValue('tags');
        const hasTags = Array.isArray(tags?.values) && tags.values.length;

        if (newKey === 'tagBoolean' && newValue && hasTags) {
          newKey = 'tags';
          newValue = {
            values: tags.values || [],
            matchAll: newValue === 'all',
          };
        } else if (newKey === 'tagBoolean' && !hasTags) {
          newValue = null;
        }

        // Handle legacy tag format
        if (
          newKey === 'tags' &&
          Array.isArray(newValue) &&
          newValue.length > 0
        ) {
          newValue = { values: newValue };
        }

        updateFilterParam(newKey, newValue);
      });
    },
    [resetFilterParams, updateFilterParam, updateParam, getFilterParamValue]
  );

  return {
    params,
    updateParam,
    filterParams,
    updateFilterParam,
    resetFilterParams,
    getFilterParamValue,
    updateFromQuickLink,
    getQuickLinkString,
    searchParams,
    setSearchParams,
  };
}
